import Constants from 'expo-constants';
import { AnalyticsReporter } from './reporter';
import { ErrorReporter } from '@/utils/sentry';
import { expoConfigEnvironment } from '@/utils/environment';
import {
  getCurrentSessionId,
  initializeSessionManager,
  resetSession,
} from './session-manager';
import { TrackingProperties } from './base-reporter';

// On native apps, Segment reports the version of the app build
// instead of the OTA update version so manually add it to the context here
const DEFAULT_CONTEXT = { appVersion: Constants.expoConfig!.version };

const enrichProperties = (
  properties: TrackingProperties,
): TrackingProperties => ({
  ...DEFAULT_CONTEXT,
  ...properties,
  sessionId: getCurrentSessionId(),
});

// Logs to console in development mode
const _log = __DEV__
  ? (type: string, ...args: unknown[]) => {
      // eslint-disable-next-line no-console
      console.log('analytics >', type, '>', ...args);
    }
  : () => {};

export interface AnalyticsType {
  identify: (userUUID: string, properties?: TrackingProperties) => void;
  // Initializes the analytics client so events can be tracked before they're sent to the destination
  initialize: (options?: { load: boolean }) => void;
  // Loads the initialized analytics client and sends any queued tracking events to the destination
  load: () => void;
  reset: () => void;
  screen: (
    to: string,
    from: string | undefined,
    properties?: TrackingProperties,
  ) => void;
  track: (name: string, properties?: TrackingProperties) => void;
}

const UnifiedAnalytics: AnalyticsType = {
  identify: (userUUID: string, properties: TrackingProperties = {}) => {
    _log('identify', userUUID);
    AnalyticsReporter.identify(userUUID, enrichProperties(properties));
  },
  initialize: (options) => {
    initializeSessionManager();

    if (__DEV__ || expoConfigEnvironment === 'development') {
      return;
    }

    _log('initialize');
    AnalyticsReporter.initialize();

    if (options?.load !== false) {
      AnalyticsReporter.load();
    }
  },
  load: () => {
    _log('load');
    AnalyticsReporter.load();
  },
  reset: () => {
    _log('reset');
    resetSession();
    AnalyticsReporter.reset();
  },
  screen: (
    to: string,
    from: string | undefined = 'ROOT',
    properties: TrackingProperties = {},
  ) => {
    const trackingName = to.toLowerCase();
    const enriched = enrichProperties(properties);
    _log('screen', trackingName, enriched);
    AnalyticsReporter.screen(trackingName, enriched);

    ErrorReporter.addBreadcrumb({
      category: 'screen',
      data: {
        ...properties,
        from,
        to,
      },
      message: `navigation: ${from} > ${to}`,
      type: 'navigation',
    });
  },
  track: (name: string, properties: TrackingProperties = {}) => {
    const trackingName = name.toLowerCase();
    const enriched = enrichProperties(properties);
    _log('track', trackingName, enriched);
    AnalyticsReporter.track(trackingName, enriched);

    ErrorReporter.addBreadcrumb({
      category: 'event',
      data: properties,
      message: name,
      type: 'ui',
    });
  },
};

// Exported this way to facilitate IDE auto-importing
export const Analytics = UnifiedAnalytics;

// Exported this way for backwards compatibility
export default UnifiedAnalytics;
